import { useState, useRef, useMemo, useCallback } from 'react'
import {
  useResearchNotes,
  useCreateNote,
  useUpdateNote,
  useDeleteNote,
  useUploadNoteMedia,
  useDeleteNoteMedia,
  useTogglePin,
  type ResearchNote,
  type MediaItem,
} from '@/api/hooks/useResearchNotes'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { toast } from 'sonner'
import {
  Plus,
  Search,
  Pin,
  PinOff,
  Trash2,
  Paperclip,
  X,
  Image as ImageIcon,
  Video,
  FileText,
  ChevronDown,
  Pencil,
  Clock,
  Tag,
  Filter,
  Send,
} from 'lucide-react'

/* ─── helpers ─── */
function formatDate(iso: string) {
  const d = new Date(iso)
  return d.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

function groupByDate(notes: ResearchNote[]) {
  const groups: Record<string, ResearchNote[]> = {}
  for (const note of notes) {
    const key = new Date(note.createdAt).toISOString().slice(0, 10)
    ;(groups[key] ??= []).push(note)
  }
  return Object.entries(groups).sort(([a], [b]) => b.localeCompare(a))
}

function isToday(dateStr: string) {
  return dateStr === new Date().toISOString().slice(0, 10)
}

function isYesterday(dateStr: string) {
  const y = new Date()
  y.setDate(y.getDate() - 1)
  return dateStr === y.toISOString().slice(0, 10)
}

function friendlyDate(dateStr: string) {
  if (isToday(dateStr)) return 'Hoje'
  if (isYesterday(dateStr)) return 'Ontem'
  return formatDate(dateStr + 'T00:00:00')
}

/* ─── media thumb ─── */
function MediaThumb({ item, onRemove }: { item: MediaItem; onRemove?: () => void }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <>
      <div className="relative group cursor-pointer" onClick={() => setExpanded(true)}>
        {item.type === 'image' ? (
          <img src={item.url} alt={item.name} className="h-20 w-20 rounded-lg object-cover border border-border" />
        ) : item.type === 'video' ? (
          <div className="h-20 w-20 rounded-lg border border-border bg-muted flex items-center justify-center">
            <Video className="h-8 w-8 text-muted-foreground" />
            <span className="absolute bottom-0.5 right-0.5 text-[9px] bg-black/70 text-white px-1 rounded">VID</span>
          </div>
        ) : (
          <div className="h-20 w-20 rounded-lg border border-border bg-muted flex items-center justify-center">
            <FileText className="h-8 w-8 text-muted-foreground" />
            <span className="absolute bottom-0.5 right-0.5 text-[9px] bg-black/70 text-white px-1 rounded">PDF</span>
          </div>
        )}
        {onRemove && (
          <button
            onClick={e => { e.stopPropagation(); onRemove() }}
            className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="text-sm truncate">{item.name}</DialogTitle>
          </DialogHeader>
          {item.type === 'image' ? (
            <img src={item.url} alt={item.name} className="w-full max-h-[75vh] object-contain rounded" />
          ) : item.type === 'video' ? (
            <video src={item.url} controls className="w-full max-h-[75vh] rounded" />
          ) : (
            <iframe src={item.url} className="w-full h-[75vh] rounded" />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

/* ─── note card ─── */
function NoteCard({
  note,
  onEdit,
  onDelete,
  onPin,
  onDeleteMedia,
}: {
  note: ResearchNote
  onEdit: (n: ResearchNote) => void
  onDelete: (id: string) => void
  onPin: (id: string) => void
  onDeleteMedia: (noteId: string, idx: number) => void
}) {
  return (
    <div className="relative flex gap-4 group">
      {/* timeline dot + line */}
      <div className="flex flex-col items-center pt-1">
        <div className={`h-3 w-3 rounded-full border-2 ${note.pinned ? 'bg-amber-400 border-amber-500' : 'bg-primary border-primary'} shrink-0`} />
        <div className="w-px flex-1 bg-border mt-1" />
      </div>

      {/* card */}
      <div className="flex-1 pb-6">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatTime(note.createdAt)}
          </span>
          <span className="text-xs font-medium text-primary/70">{note.author}</span>
          {note.pinned && (
            <Badge variant="outline" className="text-amber-500 border-amber-500/30 text-[10px] px-1.5 py-0">
              <Pin className="h-2.5 w-2.5 mr-0.5" /> Fixado
            </Badge>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card p-4 shadow-sm hover:shadow-md transition-shadow">
          {/* content */}
          <p className="whitespace-pre-wrap text-sm leading-relaxed">{note.content}</p>

          {/* media */}
          {note.media.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {note.media.map((m, i) => (
                <MediaThumb key={i} item={m} />
              ))}
            </div>
          )}

          {/* tags */}
          {note.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-3">
              {note.tags.map(tag => (
                <Badge key={tag} variant="secondary" className="text-[10px] px-1.5 py-0">
                  <Tag className="h-2.5 w-2.5 mr-0.5" />
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          {/* actions */}
          <div className="flex gap-1 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => onPin(note.id)}>
              {note.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => onEdit(note)}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-destructive hover:text-destructive" onClick={() => onDelete(note.id)}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ─── main page ─── */
export function ResearchNotes() {
  const [search, setSearch] = useState('')
  const [activeTag, setActiveTag] = useState<string | undefined>()
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [showComposer, setShowComposer] = useState(false)
  const [composerText, setComposerText] = useState('')
  const [composerTags, setComposerTags] = useState('')
  const [editingNote, setEditingNote] = useState<ResearchNote | null>(null)
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const fileRef = useRef<HTMLInputElement>(null)
  const searchTimer = useRef<ReturnType<typeof setTimeout>>()

  const { data, isLoading } = useResearchNotes({
    tag: activeTag,
    search: debouncedSearch || undefined,
  })
  const createNote = useCreateNote()
  const updateNote = useUpdateNote()
  const deleteNote = useDeleteNote()
  const uploadMedia = useUploadNoteMedia()
  const deleteMedia = useDeleteNoteMedia()
  const togglePin = useTogglePin()

  const handleSearchChange = useCallback((val: string) => {
    setSearch(val)
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => setDebouncedSearch(val), 400)
  }, [])

  const grouped = useMemo(() => groupByDate(data?.notes ?? []), [data?.notes])

  const handleSubmit = async () => {
    const text = composerText.trim()
    if (!text) return

    const tags = composerTags
      .split(',')
      .map(t => t.trim().toLowerCase())
      .filter(Boolean)

    try {
      if (editingNote) {
        await updateNote.mutateAsync({ id: editingNote.id, content: text, tags: tags.length ? tags : undefined })
        toast.success('Nota atualizada')
      } else {
        const res = await createNote.mutateAsync({ content: text, tags: tags.length ? tags : undefined })
        // upload pending files
        if (pendingFiles.length > 0) {
          for (const f of pendingFiles) {
            try {
              await uploadMedia.mutateAsync({ noteId: res.id, file: f })
            } catch {
              toast.error(`Erro ao subir ${f.name}`)
            }
          }
        }
        toast.success('Nota criada')
      }
      resetComposer()
    } catch {
      toast.error('Erro ao salvar nota')
    }
  }

  const resetComposer = () => {
    setComposerText('')
    setComposerTags('')
    setEditingNote(null)
    setPendingFiles([])
    setShowComposer(false)
  }

  const handleEdit = (note: ResearchNote) => {
    setEditingNote(note)
    setComposerText(note.content)
    setComposerTags(note.tags.join(', '))
    setShowComposer(true)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Deletar essa nota permanentemente?')) return
    try {
      await deleteNote.mutateAsync(id)
      toast.success('Nota deletada')
    } catch {
      toast.error('Erro ao deletar')
    }
  }

  const handleDeleteMedia = async (noteId: string, idx: number) => {
    try {
      await deleteMedia.mutateAsync({ noteId, mediaIndex: idx })
      toast.success('Mídia removida')
    } catch {
      toast.error('Erro ao remover mídia')
    }
  }

  const handleFilesPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (editingNote && files.length > 0) {
      // upload directly to existing note
      for (const f of files) {
        uploadMedia.mutateAsync({ noteId: editingNote.id, file: f }).catch(() => {
          toast.error(`Erro ao subir ${f.name}`)
        })
      }
    } else {
      setPendingFiles(prev => [...prev, ...files])
    }
    e.target.value = ''
  }

  return (
    <div className="space-y-4">
      {/* header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Notas de Pesquisa</h1>
          <p className="text-sm text-muted-foreground">Timeline de anotacoes, imagens e videos para referencia futura</p>
        </div>
        <Button onClick={() => { resetComposer(); setShowComposer(true) }}>
          <Plus className="h-4 w-4 mr-2" /> Nova Nota
        </Button>
      </div>

      {/* filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar nas notas..."
            value={search}
            onChange={e => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>

        {(data?.tags ?? []).length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Badge
              variant={!activeTag ? 'default' : 'outline'}
              className="cursor-pointer text-xs"
              onClick={() => setActiveTag(undefined)}
            >
              Todas
            </Badge>
            {(data?.tags ?? []).map(tag => (
              <Badge
                key={tag}
                variant={activeTag === tag ? 'default' : 'outline'}
                className="cursor-pointer text-xs"
                onClick={() => setActiveTag(activeTag === tag ? undefined : tag)}
              >
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* total */}
      {data && (
        <p className="text-xs text-muted-foreground">{data.total} nota{data.total !== 1 ? 's' : ''}</p>
      )}

      {/* composer dialog */}
      <Dialog open={showComposer} onOpenChange={open => { if (!open) resetComposer() }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingNote ? 'Editar Nota' : 'Nova Nota'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea
              placeholder="Escreva sua anotacao aqui... links, ideias, observacoes, qualquer coisa."
              value={composerText}
              onChange={e => setComposerText(e.target.value)}
              className="min-h-[120px] resize-y"
              autoFocus
            />

            <Input
              placeholder="Tags (separadas por virgula): adsense, ecpm, pesquisa"
              value={composerTags}
              onChange={e => setComposerTags(e.target.value)}
            />

            {/* pending files preview */}
            {pendingFiles.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {pendingFiles.map((f, i) => (
                  <div key={i} className="relative">
                    <div className="h-16 w-16 rounded-lg bg-muted border border-border flex items-center justify-center">
                      {f.type.startsWith('image/') ? (
                        <img src={URL.createObjectURL(f)} alt="" className="h-full w-full object-cover rounded-lg" />
                      ) : f.type.startsWith('video/') ? (
                        <Video className="h-6 w-6 text-muted-foreground" />
                      ) : (
                        <FileText className="h-6 w-6 text-muted-foreground" />
                      )}
                    </div>
                    <button
                      onClick={() => setPendingFiles(prev => prev.filter((_, j) => j !== i))}
                      className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  accept="image/*,video/*,.pdf"
                  className="hidden"
                  onChange={handleFilesPicked}
                />
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
                  <Paperclip className="h-4 w-4 mr-1" />
                  Anexar
                </Button>
              </div>
              <Button
                onClick={handleSubmit}
                disabled={!composerText.trim() || createNote.isPending || updateNote.isPending}
              >
                <Send className="h-4 w-4 mr-1" />
                {createNote.isPending || updateNote.isPending ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* timeline */}
      {isLoading ? (
        <div className="space-y-6 pt-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="space-y-3">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-28 w-full rounded-xl" />
              <Skeleton className="h-28 w-full rounded-xl" />
            </div>
          ))}
        </div>
      ) : grouped.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
          <FileText className="h-12 w-12 mb-3 opacity-40" />
          <p className="text-lg font-medium">Nenhuma nota ainda</p>
          <p className="text-sm">Clique em "Nova Nota" para comecar a documentar sua pesquisa</p>
        </div>
      ) : (
        <div className="space-y-6 pt-2">
          {grouped.map(([dateStr, notes]) => (
            <div key={dateStr}>
              {/* date header */}
              <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm pb-2 mb-2">
                <div className="flex items-center gap-2">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2">
                    {friendlyDate(dateStr)}
                  </span>
                  <div className="h-px flex-1 bg-border" />
                </div>
              </div>

              {/* notes for this date */}
              <div className="ml-2">
                {notes.map(note => (
                  <NoteCard
                    key={note.id}
                    note={note}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onPin={id => togglePin.mutateAsync(id).catch(() => toast.error('Erro ao fixar'))}
                    onDeleteMedia={handleDeleteMedia}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
